import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapPreviewCompact, getGoogleMapsUrl } from './MapPreview';
import { 
  Bell, MapPin, Car, DollarSign, Clock, User, Check, X, 
  AlertCircle, Timer, Navigation, Loader2, ChevronRight, Phone, ExternalLink,
  UserPlus, Users, Wrench, Truck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IncomingRequests({ onAccept }) {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { isConnected, registerHandler } = useWebSocket();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [showDenyDialog, setShowDenyDialog] = useState(false);
  const [denyingRequest, setDenyingRequest] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  
  // Accept & Assign dialog state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assigningRequest, setAssigningRequest] = useState(null);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(false);

  const fetchIncomingRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs/incoming-requests`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRequests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch incoming requests:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchIncomingRequests();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchIncomingRequests, 30000);
    return () => clearInterval(interval);
  }, [fetchIncomingRequests]);

  // Listen for new requests via WebSocket
  useEffect(() => {
    if (!isConnected) return;

    const unregisterNewRequest = registerHandler('new_request', (message) => {
      // Add to list if not already present
      setRequests(prev => {
        if (prev.some(r => r.request_id === message.request_id)) return prev;
        return [{ ...message, customer_details: { name: message.customer_name } }, ...prev];
      });
      
      // Show toast notification
      toast.info(`New Job: ${message.service_name}`, {
        description: `$${message.total_price} - ${message.vehicle_info}`,
        duration: 10000,
        action: {
          label: 'View',
          onClick: () => {}
        }
      });
    });

    const unregisterJobOffer = registerHandler('job_offer', (message) => {
      toast.success(`Job Offer: ${message.service_name}`, {
        description: `$${message.total_pay} - ${message.estimated_miles} miles - Accept in 5 min!`,
        duration: 15000
      });
      fetchIncomingRequests();
    });

    return () => {
      unregisterNewRequest();
      unregisterJobOffer();
    };
  }, [isConnected, registerHandler, fetchIncomingRequests]);

  const handleAccept = async (requestId, location) => {
    setProcessingId(requestId);
    try {
      const response = await axios.post(
        `${API}/jobs/request/${requestId}/quick-accept`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success('Job Accepted!', {
        description: 'Customer has been notified. Opening navigation...'
      });
      
      // Remove from list
      setRequests(prev => prev.filter(r => r.request_id !== requestId));
      
      // Auto-open navigation to customer location
      if (response.data.navigation_url) {
        window.open(response.data.navigation_url, '_blank');
      } else if (location?.lat && location?.lng) {
        const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`;
        window.open(navUrl, '_blank');
      }
      
      // Call parent callback if provided
      if (onAccept) onAccept(requestId);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept job');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeny = async () => {
    if (!denyingRequest) return;
    
    setProcessingId(denyingRequest.request_id);
    try {
      await axios.post(
        `${API}/jobs/request/${denyingRequest.request_id}/quick-deny`,
        { reason: denyReason || 'Provider unavailable' },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.info('Request Denied');
      
      // Remove from list
      setRequests(prev => prev.filter(r => r.request_id !== denyingRequest.request_id));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny request');
    } finally {
      setProcessingId(null);
      setShowDenyDialog(false);
      setDenyingRequest(null);
      setDenyReason('');
    }
  };

  const handleAcceptOffer = async (offerId) => {
    setProcessingId(offerId);
    try {
      await axios.put(
        `${API}/jobs/offers/${offerId}/accept`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success('Job Accepted!');
      fetchIncomingRequests();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept offer');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeclineOffer = async (offerId) => {
    setProcessingId(offerId);
    try {
      await axios.put(
        `${API}/jobs/offers/${offerId}/decline`,
        { reason: 'Not available' },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.info('Offer Declined');
      fetchIncomingRequests();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline offer');
    } finally {
      setProcessingId(null);
    }
  };

  // Accept & Assign to Provider (Owner/Admin only)
  const handleOpenAssignDialog = async (request) => {
    setAssigningRequest(request);
    setShowAssignDialog(true);
    setLoadingProviders(true);
    setSelectedProviderId('');
    
    try {
      const response = await axios.get(`${API}/admin/providers/available`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setAvailableProviders(response.data.providers || []);
    } catch (error) {
      toast.error('Failed to load available providers');
      setAvailableProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleAssignToProvider = async () => {
    if (!assigningRequest || !selectedProviderId) {
      toast.error('Please select a provider');
      return;
    }
    
    setProcessingId(assigningRequest.request_id);
    try {
      const response = await axios.post(
        `${API}/admin/jobs/${assigningRequest.request_id}/assign`,
        { provider_id: selectedProviderId },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success(`Job assigned to ${response.data.provider_name}!`);
      
      // Remove from list
      setRequests(prev => prev.filter(r => r.request_id !== assigningRequest.request_id));
      
      // Close dialog and navigate to provider map
      setShowAssignDialog(false);
      setAssigningRequest(null);
      setSelectedProviderId('');
      
      // Navigate to the provider tab with highlight
      if (response.data.redirect_url) {
        navigate(response.data.redirect_url);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign job');
    } finally {
      setProcessingId(null);
    }
  };

  const isOwnerOrAdmin = user?.is_owner || user?.is_admin;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');

  return (
    <div className="space-y-4">
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              <CardTitle className="text-lg">Incoming Service Requests</CardTitle>
              {pendingRequests.length > 0 && (
                <Badge className="bg-blue-600 animate-pulse">{pendingRequests.length} New</Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="text-gray-500">{isConnected ? 'Live' : 'Offline'}</span>
            </div>
          </div>
          <CardDescription>Accept jobs to start earning</CardDescription>
        </CardHeader>
        <CardContent>
          {pendingRequests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No incoming requests right now</p>
              <p className="text-sm">New jobs will appear here in real-time</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <div 
                  key={request.request_id}
                  className={`p-4 rounded-lg border-2 ${
                    request.has_offer ? 'border-orange-300 bg-orange-50' : 'border-blue-200 bg-white'
                  } hover:shadow-md transition-shadow`}
                >
                  {/* Header with Service Name and Pay */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      {/* Customer Photo */}
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {request.customer_details?.picture || request.customer_picture ? (
                          <img 
                            src={request.customer_details?.picture || request.customer_picture} 
                            alt={request.customer_details?.name || 'Customer'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-lg text-gray-900">
                            {request.service_name || request.service_type}
                          </h3>
                          {request.has_offer && (
                            <Badge className="bg-orange-500 animate-pulse">
                              <Timer className="w-3 h-3 mr-1" />
                              Offer Pending
                            </Badge>
                          )}
                          {request.urgency === 'high' && (
                            <Badge className="bg-red-500">URGENT</Badge>
                          )}
                        </div>
                        {request.customer_details?.name && (
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {request.customer_details.name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">
                        ${(request.provider_pay || (request.total_price * 0.30) || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">Your Pay (30%)</p>
                    </div>
                  </div>

                  {/* Job Details */}
                  <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Car className="w-4 h-4 text-blue-500" />
                      <span>{request.vehicle_year} {request.vehicle_make} {request.vehicle_model}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4 text-orange-500" />
                      <span>{new Date(request.created_at).toLocaleTimeString()}</span>
                    </div>
                    {request.estimated_miles && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Navigation className="w-4 h-4 text-purple-500" />
                        <span>{request.estimated_miles} miles away</span>
                      </div>
                    )}
                    {request.customer_details?.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-green-500" />
                        <a href={`tel:${request.customer_details.phone}`} className="text-green-600 hover:underline">
                          {request.customer_details.phone}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Location with Navigation */}
                  {request.location && (
                    <MapPreviewCompact 
                      location={request.location} 
                      address={request.location.address}
                    />
                  )}

                  {/* Customer Notes */}
                  {request.customer_notes && (
                    <div className="p-2 bg-gray-100 rounded text-sm text-gray-700 mt-3 mb-4">
                      <span className="font-medium">Notes:</span> {request.customer_notes}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {request.has_offer ? (
                      <>
                        <Button
                          onClick={() => handleAcceptOffer(request.offer_id)}
                          disabled={processingId === request.offer_id}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          {processingId === request.offer_id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Check className="w-4 h-4 mr-2" />
                          )}
                          Accept Offer
                        </Button>
                        <Button
                          onClick={() => handleDeclineOffer(request.offer_id)}
                          disabled={processingId === request.offer_id}
                          variant="destructive"
                          className="flex-1"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Decline
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={() => handleAccept(request.request_id, request.location)}
                          disabled={processingId === request.request_id}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          {processingId === request.request_id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              <Navigation className="w-4 h-4 mr-1" />
                            </>
                          )}
                          Accept & Navigate
                        </Button>
                        
                        {/* Accept & Assign button for Owner/Admin */}
                        {isOwnerOrAdmin && (
                          <Button
                            onClick={() => handleOpenAssignDialog(request)}
                            disabled={processingId === request.request_id}
                            className="flex-1 bg-purple-600 hover:bg-purple-700"
                            data-testid={`assign-btn-${request.request_id}`}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Accept & Assign
                          </Button>
                        )}
                        
                        <Button
                          onClick={() => {
                            setDenyingRequest(request);
                            setShowDenyDialog(true);
                          }}
                          disabled={processingId === request.request_id}
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Deny
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Offer Timer */}
                  {request.has_offer && request.offer_expires_at && (
                    <div className="mt-3 p-2 bg-orange-100 rounded flex items-center justify-center gap-2 text-orange-700 text-sm">
                      <Timer className="w-4 h-4" />
                      <span>Expires: {new Date(request.offer_expires_at).toLocaleTimeString()}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deny Dialog */}
      <Dialog open={showDenyDialog} onOpenChange={setShowDenyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deny This Request?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            You're declining the {denyingRequest?.service_name} job for ${denyingRequest?.total_price}.
          </p>
          <Textarea
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            placeholder="Reason (optional)"
            className="mt-2"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDenyDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeny}>
              Deny Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept & Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-lg" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              Accept & Assign to Provider
            </DialogTitle>
          </DialogHeader>
          
          {assigningRequest && (
            <div className="space-y-4">
              {/* Request Summary */}
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="font-semibold text-purple-900">{assigningRequest.service_name}</p>
                <p className="text-sm text-purple-700">
                  {assigningRequest.vehicle_year} {assigningRequest.vehicle_make} {assigningRequest.vehicle_model}
                </p>
                <p className="text-sm text-purple-600">
                  Customer: {assigningRequest.customer_details?.name || assigningRequest.customer_name}
                </p>
                <p className="text-lg font-bold text-green-600 mt-2">
                  Provider Pay: ${((assigningRequest.total_price || 0) * 0.30).toFixed(2)}
                </p>
              </div>

              {/* Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Provider to Assign
                </label>
                {loadingProviders ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                  </div>
                ) : availableProviders.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No available providers found</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {availableProviders.map(provider => (
                      <div
                        key={provider.user_id}
                        onClick={() => setSelectedProviderId(provider.user_id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedProviderId === provider.user_id
                            ? 'border-purple-400 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            provider.user_type === 'mechanic' ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                            {provider.user_type === 'mechanic' ? (
                              <Wrench className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Truck className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{provider.name}</p>
                            <p className="text-xs text-gray-500 capitalize">
                              {provider.user_type === 'mechanic' ? 'Mobile Mechanic' : 'Roadside Tech'}
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge className={provider.is_online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                              {provider.is_online ? 'Online' : 'Offline'}
                            </Badge>
                            {provider.current_job_id && (
                              <Badge className="bg-blue-100 text-blue-700 ml-1">On Job</Badge>
                            )}
                          </div>
                        </div>
                        {provider.phone && (
                          <p className="text-xs text-gray-400 mt-1 ml-13">
                            <Phone className="w-3 h-3 inline mr-1" />
                            {provider.phone}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignToProvider}
              disabled={!selectedProviderId || processingId === assigningRequest?.request_id}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {processingId === assigningRequest?.request_id ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Assign to Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
